<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'
import { computed, onMounted, reactive, ref } from 'vue'
import * as yup from 'yup'

import store from '@/store'

// Anti-bot configuration constants
const MIN_FORM_FILL_TIME_MS = 10000 // 10 seconds
const MATH_CAPTCHA_MAX_NUMBER = 10
const MATH_CAPTCHA_MIN_NUMBER = 1

const state = reactive({
  feedback: '',
  sending: false,
})

// Anti-bot honeypot field
const honeypot = ref('')
const formStartTime = ref(0)

// Math CAPTCHA
const mathNum1 = ref(0)
const mathNum2 = ref(0)
const mathAnswer = ref('')
const mathQuestion = computed(() => `What is ${mathNum1.value} + ${mathNum2.value}?`)
const correctAnswer = computed(() => mathNum1.value + mathNum2.value)

const generateMathChallenge = () => {
  mathNum1.value = Math.floor(Math.random() * MATH_CAPTCHA_MAX_NUMBER) + MATH_CAPTCHA_MIN_NUMBER
  mathNum2.value = Math.floor(Math.random() * MATH_CAPTCHA_MAX_NUMBER) + MATH_CAPTCHA_MIN_NUMBER
  mathAnswer.value = ''
}

onMounted(() => {
  formStartTime.value = Date.now()
  generateMathChallenge()
})

const { defineField, errors, handleSubmit, meta } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      email: yup.string().email().required(),
      message: yup.string().required(),
    }),
  ),
})

const [email, emailAttr] = defineField('email')
const [message, messageAttr] = defineField('message')

const submit = handleSubmit((values) => {
  // Anti-bot checks
  // 1. Honeypot field should be empty
  if (honeypot.value !== '') {
    state.feedback = 'Message failed to send.'
    return
  }

  // 2. Form should take at least minimum time to fill (bots are too fast)
  const timeTaken = Date.now() - formStartTime.value
  if (timeTaken < MIN_FORM_FILL_TIME_MS) {
    state.feedback = 'Please take your time filling out the form.'
    return
  }

  // 3. Math CAPTCHA must be correct
  const userAnswer = parseInt(mathAnswer.value, 10)
  if (isNaN(userAnswer) || userAnswer !== correctAnswer.value) {
    state.feedback = 'Incorrect answer to the math question. Please try again.'
    generateMathChallenge()
    return
  }

  state.sending = true
  store.dispatch.user.contact(values).then((ok: boolean) => {
    if (ok) {
      state.feedback = 'Thank you. Your message has been sent.'
    } else {
      state.feedback = 'Message failed to send.'
    }
    state.sending = false
    if (ok) {
      // Reset form on success
      email.value = ''
      message.value = ''
      mathAnswer.value = ''
      generateMathChallenge()
    }
  })
})
</script>

<template>
  <v-form id="form" @submit="submit">
    <!-- Honeypot field - hidden from users but visible to bots -->
    <input
      v-model="honeypot"
      aria-hidden="true"
      autocomplete="off"
      name="website"
      style="position: absolute; left: -9999px; width: 1px; height: 1px"
      tabindex="-1"
      type="text"
    />
    <v-row>
      <v-col lg="3" md="5" sm="7" xl="2" xs="11">
        <v-text-field
          v-model="email"
          v-bind="emailAttr"
          class="input-group--focused"
          :error-messages="errors.email"
          label="Your email"
          variant="underlined"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col lg="4" md="6" sm="8" xl="3" xs="12">
        <v-textarea
          v-model="message"
          v-bind="messageAttr"
          class="my-0 py-0"
          :error-messages="errors.message"
          placeholder="Message"
          variant="underlined"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col lg="3" md="5" sm="7" xl="2" xs="11">
        <v-text-field v-model="mathAnswer" class="input-group--focused" :label="mathQuestion" required type="number" variant="underlined" />
      </v-col>
    </v-row>
    <v-row v-if="state.feedback" class="my-3">
      <v-col lg="4" md="6" sm="8" xl="3" xs="12">
        {{ state.feedback }}
      </v-col>
    </v-row>
    <v-row>
      <v-col lg="3" md="5" sm="7" xl="2" xs="11">
        <v-btn block color="primary" :disabled="!meta.valid || state.sending" type="submit">
          Send
          <v-progress-circular v-if="state.sending" class="mx-4" indeterminate size="20" />
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
